import { useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEntity } from '../contexts/EntityContext';
import { useFeatureFlag, FeatureFlagKey } from './useFeatureFlags';
import { fetchPinnedProducts, savePinnedProducts } from '../services/pinnedProductsStorage';

export const MAX_PINS = 7;

export function pinnedProductsQueryKey(entityId: number | undefined, userId: string | undefined) {
  return ['pinnedProducts', entityId, userId] as const;
}

export function usePinnedProducts() {
  const { activeEntity } = useEntity();
  const { user } = useAuth0();
  const entityId = activeEntity?.entityId;
  const userId = user?.sub;
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => pinnedProductsQueryKey(entityId, userId), [entityId, userId]);
  const allProductsEnabled = useFeatureFlag(FeatureFlagKey.PSP_UI_ALL_PRODUCTS_PAGE);

  const { data: pinnedPathsArray = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => fetchPinnedProducts(entityId!, userId!),
    enabled: entityId != null && userId != null && allProductsEnabled,
    staleTime: Infinity,
  });

  const pinnedPaths = useMemo(() => new Set(pinnedPathsArray), [pinnedPathsArray]);

  const atPinLimit = pinnedPaths.size >= MAX_PINS;

  const { mutate } = useMutation<void, Error, { next: string[] }>({
    mutationFn: ({ next }) => savePinnedProducts(entityId!, userId!, next),
    onMutate: async ({ next }) => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<string[]>(queryKey, next);
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const togglePin = useCallback(
    (basePath: string) => {
      if (entityId == null || userId == null) return;
      const next = new Set(pinnedPaths);
      if (next.has(basePath)) {
        next.delete(basePath);
      } else {
        if (pinnedPaths.size >= MAX_PINS) return;
        next.add(basePath);
      }
      mutate({ next: [...next] });
    },
    [entityId, userId, pinnedPaths, mutate]
  );

  const isPinned = useCallback((basePath: string) => pinnedPaths.has(basePath), [pinnedPaths]);

  return { pinnedPaths, isPinned, togglePin, isLoading, atPinLimit };
}
